<script lang="ts">
export default {
  name: 'LandingPage',
};
</script>

<script setup lang="ts">
import { computed, inject, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Store } from 'vuex';
import { AppState } from '@/store';
import {
  Bell,
  Building,
  Building2Icon,
  BarChart3Icon,
  Boxes,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartLine,
  ChartNoAxesCombined,
  ClipboardList,
  CloudCog,
  Database,
  Factory,
  FileTextIcon,
  FileQuestionIcon,
  Globe,
  History,
  KeyRound,
  Loader,
  MessageSquare,
  NotebookText,
  Palette,
  Percent,
  Search,
  SettingsIcon,
  SquarePen,
  Star,
  Table,
  UsersIcon,
  UserIcon,
} from 'lucide-vue-next';
import { IsUserAnAdmin, UserHasPermission, IsUserAClient, IsMarketUser, GetAllowedCarrierIds, GetAllowedWholesalerIds } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';
import UserFeedbackModal from '@/components/UserFeedbackModal.vue';
import UniversalSearch from '@/components/UniversalSearch.vue';
import { colors } from '@/utilities/Color';

const iconMap: Record<string, any> = {
  Bell,
  Building,
  Building2Icon,
  BarChart3Icon,
  Boxes,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartLine,
  ChartNoAxesCombined,
  ClipboardList,
  CloudCog,
  Database,
  Factory,
  FileTextIcon,
  FileQuestionIcon,
  Globe,
  History,
  KeyRound,
  Loader,
  MessageSquare,
  NotebookText,
  Palette,
  Percent,
  Search,
  SettingsIcon,
  SquarePen,
  Star,
  Table,
  UsersIcon,
  UserIcon,
};

// -----
// types
// -----

interface RecentNavLink {
  title: string;
  to?: string;
  icon: string;
  timestamp: number;
}

interface NavigationLink {
  title: string;
  to?: string;
  icon: any;
  action?: string;
  hidden?: boolean;
}

interface NavigationGroup {
  groupId: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  iconColor: string;
  hoverColor: string;
  isAdmin?: boolean;
  links: NavigationLink[];
}

// -----
// data
// -----

const store = inject<Store<AppState>>('store');
const router = useRouter();
const showUserFeedbackModal = ref(false);
const recentNavigation = ref<RecentNavLink[]>([]);

// -----
// computed
// -----

const isAdmin = computed(() => IsUserAnAdmin(store?.state.user));

const canViewNotifications = computed(() => isAdmin.value || UserHasPermission(store?.state.user, UserPermission.NotificationsRead));

const hasRecentNavigation = computed(() => recentNavigation.value.length > 0);

const navigationGroups = computed<NavigationGroup[]>(() =>
  [
    {
      groupId: 'clients',
      name: 'Clients',
      description: 'Comprehensive client tools for portfolio management',
      icon: UsersIcon,
      color: colors.medium_slate,
      iconColor: colors.medium_slate,
      hoverColor: colors.medium_slate,
      links: [
        {
          title: 'Client Management',
          to: '/accounts',
          icon: Building,
        },
        {
          title: 'Analytics Hub',
          to: '/analytics',
          icon: ChartColumn,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
        {
          title: 'Towers',
          to: '/v2/tower',
          icon: ChartColumnStacked,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
      ],
    },
    {
      groupId: 'book-of-business',
      name: 'Book of Business',
      description: 'Advanced policy exploration',
      icon: FileTextIcon,
      color: colors.light_gray,
      iconColor: colors.light_gray,
      hoverColor: colors.light_gray,
      links: [
        {
          title: 'Policy Explorer',
          to: '/policy/explorer',
          icon: Search,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
      ],
    },
    {
      groupId: 'marketplace',
      name: 'Marketplace',
      description: 'Carrier performance analytics, wholesaler distribution insights, and market intelligence',
      icon: Building2Icon,
      color: colors.pale_blue,
      iconColor: colors.pale_blue,
      hoverColor: colors.pale_blue,
      links: [
        {
          title: 'Carrier Analytics',
          to: '/carriers',
          icon: Table,
          hidden: IsUserAClient(store?.state.user) || (IsMarketUser(store?.state.user) && GetAllowedCarrierIds(store?.state.user).length === 0),
        },
        {
          title: 'Wholesaler Analytics',
          to: '/wholesaler-group',
          icon: BrickWall,
          hidden: IsUserAClient(store?.state.user) || (IsMarketUser(store?.state.user) && GetAllowedWholesalerIds(store?.state.user).length === 0),
        },
        {
          title: 'Carrier Survey Results',
          to: '/carriers/surveys',
          icon: ClipboardList,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
        {
          title: 'AM Best Ratings',
          to: '/am-best',
          icon: Star,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
      ],
    },
    {
      groupId: 'management',
      name: 'Management',
      description: 'Financial analytics, yield analysis, and comprehensive business reporting',
      icon: BarChart3Icon,
      color: colors.specialty_teal,
      iconColor: colors.specialty_teal,
      hoverColor: colors.specialty_teal,
      links: [
        {
          title: 'Management Reporting',
          to: '/management-analytics',
          icon: ChartLine,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
        {
          title: 'Reporting Hub',
          to: '/reporting',
          icon: Factory,
          hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
        },
        {
          title: 'Revenue Dashboard',
          to: '/revenue-dashboard',
          icon: ChartNoAxesCombined,
          hidden: !IsUserAnAdmin(store?.state.user),
        },
        {
          title: 'Yield Analysis',
          to: '/yield-analysis',
          icon: Percent,
          hidden: !IsUserAnAdmin(store?.state.user),
        },
      ],
    },
    {
      groupId: 'user-system',
      name: 'User & System',
      description: 'Manage your account settings and access support resources',
      icon: UserIcon,
      color: colors.dark_slate,
      iconColor: colors.dark_slate,
      hoverColor: colors.dark_slate,
      links: [
        ...(canViewNotifications.value
          ? [
              {
                title: 'Notifications',
                to: '/notifications',
                icon: Bell,
              },
              {
                title: 'Notification Settings',
                to: '/notifications/preferences',
                icon: SettingsIcon,
              },
            ]
          : []),
        {
          title: 'Demo Environment',
          action: 'demo',
          icon: Globe,
        },
        {
          title: 'Submit Feedback',
          action: 'feedback',
          icon: MessageSquare,
        },
        {
          title: 'Help Center',
          action: 'help',
          icon: FileQuestionIcon,
        },
      ],
    },
    ...(isAdmin.value
      ? [
          {
            groupId: 'admin',
            name: 'Admin',
            description: 'System administration and configuration tools',
            icon: SettingsIcon,
            color: colors.agency_red,
            iconColor: colors.agency_red,
            hoverColor: colors.agency_red,
            isAdmin: true,
            links: [
              {
                title: 'Carrier Group Mapping',
                to: '/admin/carrier-issuer',
                icon: NotebookText,
              },
              {
                title: 'Policy Type Mapping',
                to: '/admin/policy-type',
                icon: SquarePen,
              },
              {
                title: 'Wholesaler Mapping',
                to: '/admin/wholesaler',
                icon: Boxes,
              },
              {
                title: 'Data Quality',
                to: '/admin/data-quality',
                icon: Database,
              },
              {
                title: 'Cache Management',
                to: '/admin/cache',
                icon: CloudCog,
              },
              {
                title: 'Permissions Management',
                to: '/admin/permissions',
                icon: KeyRound,
              },
              {
                title: 'AI Data Extraction',
                to: '/admin/ai',
                icon: Loader,
              },
              {
                title: 'Reporting',
                to: '/admin/reporting',
                icon: ChartColumn,
              },
              ...(import.meta.env.MODE === 'development'
                ? [
                    {
                      title: 'Custom Components',
                      to: '/admin/component-showcase',
                      icon: Palette,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ].filter((group) => group.links.length > 0),
);

// ----------
// functions
// ----------

function loadRecentNavigation() {
  try {
    const stored = sessionStorage.getItem('cac-recent-navigation');
    if (stored) {
      const parsed = JSON.parse(stored) as RecentNavLink[];
      // Filter out items older than 24 hours and limit to 3
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      recentNavigation.value = parsed.filter((item) => item.timestamp > dayAgo).slice(0, 5);
    }
  } catch (error) {
    console.warn('Failed to load recent navigation from session storage:', error);
    recentNavigation.value = [];
  }
}

function saveRecentNavigation() {
  try {
    sessionStorage.setItem('cac-recent-navigation', JSON.stringify(recentNavigation.value));
  } catch (error) {
    console.warn('Failed to save recent navigation to session storage:', error);
  }
}

function addToRecentNavigation(link: { title: string; to?: string; icon: any }) {
  if (!link.to) return; // Don't track actions, only navigation

  let iconName = typeof link.icon === 'string' ? link.icon : '';
  if (typeof link.icon !== 'string') {
    iconName = Object.keys(iconMap).find((key) => iconMap[key] === link.icon) || '';
  }

  const newItem: RecentNavLink = {
    title: link.title,
    to: link.to,
    icon: iconName,
    timestamp: Date.now(),
  };

  // Remove existing item with same path if it exists
  const filtered = recentNavigation.value.filter((item) => item.to !== link.to);

  // Add new item to the beginning and limit to 5
  recentNavigation.value = [newItem, ...filtered].slice(0, 5);

  saveRecentNavigation();
}

function handleNavigation(link: any) {
  if (link.to) {
    addToRecentNavigation(link);
    router.push(link.to);
  } else if (link.action) {
    handleAction(link.action);
  }
}

function handleAction(action: string) {
  switch (action) {
    case 'demo':
      window.open('https://demo.platform.cacspecialty.com/', '_blank');
      break;
    case 'feedback':
      showUserFeedbackModal.value = true;
      break;
    case 'help':
      window.open('https://cobbs-allen.sharefile.com/f/fodf52cc-2817-4922-bf95-8473a458c56a', '_blank');
      break;
  }
}

// ----------
// lifecycle
// ----------

onMounted(() => {
  loadRecentNavigation();
});
</script>

<template>
  <UserFeedbackModal
    :show="showUserFeedbackModal"
    @close="showUserFeedbackModal = false" />
  <div class="tw:h-full tw:bg-gray-50">
    <!-- Main Content -->
    <main class="tw:mx-auto tw:max-w-7xl tw:px-4 tw:py-1 tw:pt-4 tw:sm:px-6 tw:lg:px-4">
      <!-- Universal Search -->
      <div class="tw:mb-4">
        <UniversalSearch />
      </div>

      <!-- Category Sections -->
      <div class="tw:flex tw:h-full tw:flex-col tw:gap-2">
        <!-- Quick Navigation Row -->
        <div
          v-if="hasRecentNavigation"
          class="tw:flex tw:flex-col tw:gap-2 tw:sm:mr-auto tw:sm:ml-14 tw:sm:flex-row">
          <div class="tw:flex tw:items-center tw:text-xs tw:font-medium tw:text-gray-500">
            <History
              :size="17"
              class="tw:mr-1" />
            Recent:
          </div>
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <template
              v-for="link in recentNavigation"
              :key="link.to">
              <button
                class="tw:flex tw:items-center tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2 tw:text-xs tw:font-medium tw:text-gray-700 tw:shadow-xs tw:transition-all tw:duration-150 tw:hover:bg-gray-50"
                @click="router.push(link.to!)">
                <div class="tw:mr-2 tw:shrink-0 tw:self-start">
                  <component
                    :is="iconMap[link.icon]"
                    class="tw:h-4 tw:w-4" />
                </div>
                {{ link.title }}
              </button>
            </template>
          </div>
        </div>
        <div v-else></div>

        <!-- All Cards Grid -->
        <div class="tw:flex tw:flex-col tw:gap-5">
          <div class="tw:flex tw:flex-wrap tw:justify-center tw:gap-5">
            <template
              v-for="group in navigationGroups"
              :key="group.groupId">
              <!-- Category Card -->
              <div
                :style="{ borderColor: group.color + '45' }"
                class="tw:flex tw:w-full tw:flex-col tw:overflow-hidden tw:rounded-lg tw:border tw:bg-white tw:shadow-md tw:transition-all tw:duration-150 tw:sm:w-[23rem]">
                <!-- Category Header -->
                <div class="tw:flex tw:shrink-0 tw:items-center tw:border-b tw:border-gray-100 tw:p-3">
                  <div class="tw:mr-2 tw:shrink-0 tw:self-start tw:pt-1">
                    <component
                      :is="group.icon"
                      :style="{ color: group.iconColor }"
                      class="tw:h-4 tw:w-4" />
                  </div>
                  <div class="tw:min-w-0 tw:flex-1">
                    <h3 class="tw:truncate tw:text-sm tw:font-semibold tw:text-gray-900">{{ group.name }}</h3>
                    <p class="tw:mt-0.5 tw:h-8 tw:text-xs tw:leading-4 tw:text-gray-500">{{ group.description }}</p>
                  </div>
                </div>

                <!-- Links List -->
                <div class="tw:flex-1 tw:divide-y tw:divide-gray-100 tw:overflow-y-auto">
                  <template
                    v-for="link in group.links"
                    :key="link.title">
                    <button
                      v-if="!link.hidden"
                      :style="{ '--hover-color': group.hoverColor }"
                      class="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:border-none tw:bg-transparent tw:px-3 tw:py-2.5 tw:text-left tw:transition-colors tw:hover:bg-gray-100"
                      @click="handleNavigation(link)">
                      <div class="tw:mr-2 tw:shrink-0 tw:self-start">
                        <component :is="link.icon" :size="18" class="tw:text-gray-500" />
                      </div>

                      <div class="tw:min-w-0 tw:flex-1">
                        <div class="tw:truncate tw:text-xs tw:font-medium tw:text-gray-700">{{ link.title }}</div>
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
